// src/app/login/cookie-utils.ts

export interface CookieOptions {
  expiresAtMs?: number;
  path?: string;
  domain?: string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
}

/**
 * Setzt ein Cookie.
 * - DEV (http://localhost)  -> SameSite=Lax, ohne Secure
 * - PROD (https://*.muessig.app) -> SameSite=None; Secure
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const {
    expiresAtMs,
    path = '/',
    domain,
    sameSite,
    secure,
  } = options;

  const parts: string[] = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
  ];

  if (expiresAtMs) {
    const d = new Date(expiresAtMs);
    parts.push(`Expires=${d.toUTCString()}`);
  }

  parts.push(`Path=${path}`);

  if (domain) {
    parts.push(`Domain=${domain}`);
  }

  const isHttps = window.location.protocol === 'https:';

  const finalSameSite: 'Lax' | 'Strict' | 'None' =
    sameSite ?? (isHttps ? 'None' : 'Lax');
  parts.push(`SameSite=${finalSameSite}`);

  const finalSecure = secure ?? (isHttps && finalSameSite === 'None');
  if (finalSecure && isHttps) {
    parts.push('Secure');
  }

  const cookieString = parts.join('; ');
  document.cookie = cookieString;
}

export function getCookie(name: string): string | null {
  const target = `${encodeURIComponent(name)}=`;
  const decoded = decodeURIComponent(document.cookie ?? '');
  const parts = decoded.split(';');

  for (let part of parts) {
    part = part.trim();
    if (part.startsWith(target)) {
      return part.substring(target.length);
    }
  }
  return null;
}

export function deleteCookie(name: string, domain?: string, path: string = '/'): void {
  const parts: string[] = [
    `${encodeURIComponent(name)}=`,
    'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
    `Path=${path}`,
  ];
  if (domain) {
    parts.push(`Domain=${domain}`);
  }
  document.cookie = parts.join('; ');
}
